import { formatRelative } from "@veil/ui/lib/format";
import { cn } from "@veil/ui/lib/utils";
import { RenderStatusBar } from "@/components/render-status-bar";
import type { ClusterRow, ClusterStats } from "./types";

/**
 * Cockpit command bar. One sticky row that names the cluster, surfaces the
 * numbers an operator glances at before doing anything, and parks the primary
 * Render & Reload CTA so it is always one click away.
 *
 * The reachability and weight-share readouts used to be computed here from a
 * fetched worker list. Those belong next to the workers they describe, and this
 * page no longer lists workers, so the bar reads the cluster's own counts and
 * leaves per-worker health to /workers.
 */
export function CommandBar({
	cluster,
	stats,
}: {
	cluster: ClusterRow;
	stats: ClusterStats | undefined;
}) {
	const totalHosts = stats?.totalHosts ?? 0;
	const enabledHosts = stats?.enabledHosts ?? 0;
	const dirty = stats?.dirty === true;

	return (
		<header
			className={cn(
				"sticky top-(--header-height) z-10 -mx-4 -mt-4 mb-2 border-b backdrop-blur md:-mx-6 md:-mt-6",
				dirty
					? "border-amber-500/30 bg-amber-50/70 dark:bg-amber-500/[0.04]"
					: "border-border/60 bg-background/95 supports-[backdrop-filter]:bg-background/70",
			)}
		>
			<div className="flex flex-col gap-4 px-4 py-4 md:flex-row md:items-center md:gap-6 md:px-6 md:py-4">
				<div className="min-w-0 shrink-0 space-y-1">
					<div className="font-mono text-[0.68rem] uppercase tracking-[0.16em] text-muted-foreground">
						Load balancer
					</div>
					<h1 className="font-heading text-xl font-semibold leading-tight tracking-tight">
						{cluster.name}
					</h1>
				</div>

				<div className="flex min-w-0 flex-1 flex-wrap items-stretch gap-x-6 gap-y-3 md:justify-end md:gap-x-8">
					<Metric
						label="In pool"
						value={`${enabledHosts}/${totalHosts}`}
						title={`${enabledHosts} of ${totalHosts} workers are included in the upstream pool`}
					/>
					<Metric
						label="Last render"
						value={
							stats?.lastRender?.finishedAt
								? formatRelative(stats.lastRender.finishedAt)
								: "never"
						}
						title={
							stats?.lastRender?.finishedAt
								? new Date(stats.lastRender.finishedAt).toLocaleString()
								: "openresty has never been reloaded from this manager"
						}
					/>
				</div>

				<RenderStatusBar
					stats={stats}
					clusterName={cluster.name}
					className="shrink-0 md:ml-2"
				/>
			</div>
		</header>
	);
}

function Metric({
	label,
	value,
	title,
}: {
	label: string;
	value: string;
	title?: string;
}) {
	return (
		<div className="flex flex-col justify-center" title={title}>
			<span className="font-mono text-[0.68rem] uppercase tracking-[0.16em] text-muted-foreground">
				{label}
			</span>
			<span className="font-mono text-base tabular-nums text-foreground">
				{value}
			</span>
		</div>
	);
}
